/**
 * Day 98.5 (V1 item #1) — pure helpers for the global top-bar search.
 *
 * The frontend-side logic that earns unit tests, kept free of React/Supabase so
 * the TopBar GlobalSearch component stays a thin shell over tested logic:
 *   - classifyQuery: smart-search identifier detection (ASIN / UPC vs free text)
 *   - resultHref:    per-entity deep-link target (the verified Day-98.5 routes)
 *   - primaryResult: the row Enter navigates to (identifier → product; else top rank)
 *   - groupResults:  group + order rows for the dropdown's labelled sections
 *
 * Row shape mirrors the global_search RPC (uniform across all four entities).
 */

export type SearchEntityType = 'product' | 'po' | 'manufacturer' | 'file';

/** One row from the global_search RPC. `sublabel` is nullable (manufacturers/files
 *  return NULL); the generated Supabase types widen it to string, so the client
 *  narrows to this interface. */
export interface GlobalSearchRow {
  entity_type: SearchEntityType;
  entity_key: string;
  label: string;
  sublabel: string | null;
  rank: number;
}

// Amazon ASIN: 'B0' + 8 alphanumerics. UPC-A: 12 digits.
const ASIN_RE = /^B0[A-Z0-9]{8}$/i;
const UPC_RE = /^\d{12}$/;

/** Smart-search classifier — is the trimmed query an ASIN, a UPC, or free text? */
export function classifyQuery(raw: string): 'asin' | 'upc' | 'text' {
  const q = raw.trim();
  if (ASIN_RE.test(q)) return 'asin';
  if (UPC_RE.test(q)) return 'upc';
  return 'text';
}

/** Deep-link target for a result. Products nav by isg_sku (route
 *  /catalog/products/:isgSku); POs by id; manufacturers + files have no detail route
 *  (deep-link to their section/page — the 4 Directory entity modals stay pending
 *  per BACKLOG L12). */
export function resultHref(entityType: SearchEntityType, entityKey: string): string {
  switch (entityType) {
    case 'product':
      return `/catalog/products/${encodeURIComponent(entityKey)}`;
    case 'po':
      return `/purchasing/purchase-orders/${entityKey}`;
    case 'manufacturer':
      return '/directory/manufacturers';
    case 'file':
      return '/files';
  }
}

/** The row Enter navigates to when the user hasn't arrow-selected one. For an
 *  ASIN/UPC query the highest-ranked product hit (the identifier IS a product);
 *  otherwise the highest-ranked row overall. Order-independent (picks by rank),
 *  so it does not assume the RPC's UNION ALL output is pre-sorted. */
export function primaryResult(rows: GlobalSearchRow[], query: string): GlobalSearchRow | null {
  if (rows.length === 0) return null;
  const maxByRank = (list: GlobalSearchRow[]): GlobalSearchRow =>
    list.reduce((best, r) => (r.rank > best.rank ? r : best));
  const cls = classifyQuery(query);
  if (cls === 'asin' || cls === 'upc') {
    const products = rows.filter((r) => r.entity_type === 'product');
    if (products.length > 0) return maxByRank(products);
  }
  return maxByRank(rows);
}

/** Fixed display order + section labels for the grouped dropdown. */
const ENTITY_ORDER: SearchEntityType[] = ['product', 'po', 'manufacturer', 'file'];
export const ENTITY_LABELS: Record<SearchEntityType, string> = {
  product: 'Products',
  po: 'Purchase Orders',
  manufacturer: 'Manufacturers',
  file: 'Files',
};

/** Group rows by entity type (fixed ENTITY_ORDER), rank-sorted within each
 *  group. Empty groups are omitted. */
export function groupResults(
  rows: GlobalSearchRow[]
): Array<{ type: SearchEntityType; rows: GlobalSearchRow[] }> {
  const byType = new Map<SearchEntityType, GlobalSearchRow[]>();
  for (const r of rows) {
    const list = byType.get(r.entity_type);
    if (list) list.push(r);
    else byType.set(r.entity_type, [r]);
  }
  for (const list of byType.values()) list.sort((a, b) => b.rank - a.rank);
  return ENTITY_ORDER.filter((t) => byType.has(t)).map((t) => ({
    type: t,
    rows: byType.get(t) as GlobalSearchRow[],
  }));
}
